import { FC } from 'react';
import clsx from 'clsx';
import { Box, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { useShowMoreText } from './useShowMoreText';
import s from './ShowMoreText.module.scss';

type Props = {
  originalText: string | undefined | null;
  maxChars?: number;
  maxLines?: number;
  showMoreInitialState?: boolean;
  textSize?: 'small' | 'medium';
  inlineButton?: boolean;
  buttonWeight?: 'thin' | 'normal' | 'bold';
  showMoreButtonText?: string;
  showLessButtonText?: string;
  isMobileView?: boolean;
  isWideDisplay?: boolean;
  className?: string;
  handleShowMoreClick?: () => void;
  handleShowLessClick?: () => void;
  dataHookPrefix?: string;
};

const ShowMoreText: FC<Props> = ({
  originalText,
  maxChars,
  maxLines,
  showMoreInitialState,
  textSize = 'small',
  inlineButton = false,
  buttonWeight = 'normal',
  showMoreButtonText,
  showLessButtonText,
  isMobileView = false,
  isWideDisplay = false,
  className,
  handleShowMoreClick,
  handleShowLessClick,
  dataHookPrefix = '',
}) => {
  const { t } = useTranslation();
  const {
    showingMore,
    needsTruncation,
    truncatedText,
    remainingText,
    handleToggle,
  } = useShowMoreText({
    originalText,
    maxChars,
    maxLines,
    showMoreInitialState,
    handleShowMoreClick,
    handleShowLessClick,
  });

  const commonTextProps = {
    size: textSize,
    weight: 'thin' as const,
    style: {
      wordBreak: 'break-word' as const,
    },
    dataHook: `${dataHookPrefix}show-more-text`,
  };

  const buttonProps = {
    dataHook: `${dataHookPrefix}show-more-text-button`,
    skin: 'dark' as const,
    underline: 'always' as const,
    size: textSize,
    weight: buttonWeight,
    onClick: handleToggle,
    children: showingMore
      ? showLessButtonText || t('show-less-text-button')
      : showMoreButtonText || t('show-more-text-button'),
  };

  if (!originalText) return null;

  const shouldShowButtonInline = isMobileView || inlineButton;

  const textContentClassName = clsx(
    s.textContent,
    maxLines && !showingMore && !isMobileView
      ? s.lineClamped
      : showingMore
      ? s.expanded
      : '',
  );

  return (
    <Box
      className={clsx(
        !isWideDisplay && s.showMoreTextContainer,
        isWideDisplay && s.widerShowMoreTextContainer,
        isMobileView && s.mobile,
        className,
      )}
      dataHook="show-more-text-container"
      direction={isMobileView ? undefined : 'vertical'}
    >
      <Box
        className={textContentClassName}
        style={{ '--max-lines': maxLines } as React.CSSProperties}
      >
        <Text {...commonTextProps}>
          {truncatedText}
          <span className={remainingText && !showingMore ? '' : s.hide}>
            ...
          </span>
          <span
            className={remainingText && showingMore ? '' : s.hide}
            data-hook={`${dataHookPrefix}show-more-text-extra`}
          >
            {remainingText.trimEnd()}
          </span>
          &nbsp;
          {shouldShowButtonInline && needsTruncation && (
            <TextButton
              {...buttonProps}
              className={clsx(s.moreLessButton, isMobileView && s.mobile)}
            />
          )}
        </Text>
      </Box>
      {!shouldShowButtonInline && needsTruncation && (
        <TextButton {...buttonProps} className={s.moreLessButton} />
      )}
    </Box>
  );
};

export default ShowMoreText;
